/** The strategic turn: which hero goes where, and what it is worth going for.
 *
 * One Dijkstra pass per hero and no second pathfinder. `enumerateLegalCommands`
 * already runs `computeReachable` to build the `MOVE_HERO` family (3.3), and
 * that `ReachableSet` is handed straight to the scorer - cost to any cell is a
 * lookup, and the concrete path is `pathTo`. Writing an A* here would be a
 * second implementation of movement costing, which is the same mistake as a
 * closed-form battle estimator, one layer up.
 *
 * Everything is scored as value over distance: what the tile is worth in
 * gold-equivalents, divided by the movement points it takes to stand on it.
 * That single ratio is what makes a chest two tiles away beat a mine ten tiles
 * away and lose to a mine three tiles away, without a table of special cases.
 */

import {
  BLOOD_RITUAL_CREATURES, BLOOD_RITUAL_MANA_PER_TIER,
  DIMENSION_DOOR_MOVEMENT_COST, HARVEST_BY_MASTERY, METEOR_HP_PER_POWER,
  RESOURCES, SCRY_RANGE_BY_MASTERY, TERRAINS,
  TRANSMUTE_GOLD_PER_UNIT, TRANSMUTE_UNITS, UNREACHABLE_COST,
  VIEW_RANGE_BY_MASTERY,
  adventureRuleOf, bloodRitualSlot, castGate, computeReachable, dimensionDoorRange,
  executeCommand, carriesGrail, hasSpellbook, isMoveFamily,
  isReachable, makeCtx, masteryFor, maxMana, maxSpellLevel, moveRulesFor, pathCost, pathLegs,
  serviceValue, spellCostFor, tileDistance, transmuteSource,
} from "@game/core";
import type {
  ArmySlot, ArtifactDefId, CreatureDefId, GameCommand, HeroId,
  HeroState, LegalCommand, MapObjectDef, ObjectId, ObservedObject, Payload,
  PortalLink, ReachableSet, ResourceKind, SpellDef, SpellDefId, TerrainKind, TownState,
} from "@game/core";
import type { KnownEnemy, Shadow } from "./shadow.js";
import type { DifficultyProfile } from "./difficulty.js";
import { compareCandidates, type Candidate } from "./types.js";
import {
  MINE_HORIZON_DAYS, armyStrength, strongestOwnArmy,
} from "./evaluate.js";
import { NO_HERO, probeArmies, verdictOf, type FightVerdict } from "./simulate.js";
import { presumedArmy, presumedFort } from "./threat.js";
import { mainHero, threatenedTowns } from "./strategy.js";

// ---- weights

/** One plain step costs 100 movement points, so this is "one tile of walking". */
export const DISTANCE_BASE = 100;
/** Gold-equivalent of one unexplored neighbour of a reachable tile. */
export const FRONTIER_UNIT = 350;
/**
 * What one unexplored neighbour is worth while nobody knows where the enemy is.
 *
 * Exploring is ordinarily a chore worth a fraction of a chest, and that is the
 * right price for it once the map is understood. Before then it is the war:
 * a computer that has never seen a rival capital cannot march on one, cannot
 * lose the game to one, and will happily spend two hundred days recruiting
 * peasants ten tiles from an opponent doing the same. Measured, on seed 1000:
 * neither player saw the other's town once in two hundred days, and the ordinary
 * frontier score - two thousand eight hundred over a twenty-tile walk, against
 * a shrine next door worth six thousand over one - is why. So while no enemy
 * town is known, the frontier is scored as a conquest target: multiplied by
 * this, and walked to with `conquestPatience` rather than as if every day of
 * the journey burnt its value away.
 */
export const SCOUT_WAR_BP = 26000;
/** Experience is worth something, but a hero level is not a mine. */
export const EXPERIENCE_TO_GOLD_BP = 4000;
/** A resource the AI is short of for its next building is worth double. */
export const SHORTAGE_BP = 20000;
export const NEUTRAL_BP = 10000;
/** A fight the AI wins but is gutted by is worth a quarter of the prize. */
export const COSTLY_BP = 2500;
/**
 * An enemy town is the win condition; it outranks any pile of gold.
 *
 * Thirty thousand did not. A gem mine is worth `MINE_HORIZON_DAYS` of gems -
 * 37,500 - so a town priced at 30,000, discounted for the fog to 29,700, was
 * literally worth less to this computer than a mine, and the measurement says
 * so: on seed 12161 at day 200 `p3` held a million points of army, its own
 * probe called the enemy town twelve tiles away a **win**, and it scored that
 * win at 74,250 against 104,529 for fetching a stack out of its own hall. It
 * fetched the stack. Every day, for a hundred days.
 *
 * A town is a hall, a week of growth, a mage guild, a fort and one fewer rival
 * seat on the board; nothing else on the map is worth what it is worth, and the
 * price now says that. Aggression stays a difficulty knob on top
 * (`aggressionBp`), and how *winnable* the fight is remains a matter of fact
 * settled by the probe - raising what a town is worth does not make a losing
 * siege look survivable, because the verdict gates the value rather than
 * scaling it.
 */
export const ENEMY_TOWN_VALUE = 90000;
export const ENEMY_HERO_VALUE = 9000;
/**
 * A war is ended by the *last* town, not by a town, and until this weight
 * existed nothing in the AI knew the difference. An opponent down to one seat
 * loses the game when it falls; an opponent with four loses a building. Scored
 * flat, both were `ENEMY_TOWN_VALUE`, so the computer that had ground a rival
 * down to his capital went and took a mine instead - measured on seed 12161,
 * where two survivors circled each other for two hundred days with every
 * neutral town already taken.
 *
 * The reading is off the public scoreboard (`ObservedOpponent.townCount`),
 * which every player sees fog or no fog, so this is pressing an advantage the
 * AI can legitimately see rather than a fog leak.
 */
export const LAST_TOWN_BP = 40000;
/** Down to two: the war is nearly decided, and worth deciding. */
export const SECOND_LAST_TOWN_BP = 18000;
/**
 * On top of the above, for a kingdom with no hero left on the board. It cannot
 * reinforce the seat, it cannot retake it, and it cannot threaten ours while we
 * march - so this is the cheapest win on the map and should be scored like one.
 */
export const NO_HERO_BP = 15000;
/**
 * Our own garrison, waiting in a town for somebody to carry it.
 *
 * This is the single biggest thing the AI was not doing, and the measurement is
 * blunt: on seed 12161 at day 300 the two survivors held 3.19M and 3.30M of
 * army *in their garrisons* while their heroes carried 58k and 532k. Recruiting
 * happens whether or not a hero is standing in the town, and a hero has no
 * reason to be there once `available` is empty - so every creature the kingdom
 * bought sat in the hall for the rest of the game. Neither side could ever
 * field a stack big enough to take a defended town, so neither side ever won.
 *
 * Scored as the army it is, discounted a little because a stack at home is
 * worth slightly less than the same stack at the front, capped at what a town
 * is worth (`COLLECT_CAP`, and read that one next), and walked to with conquest
 * patience because an army does not spoil while you march to it. The ratio does
 * the rest: a five-thousand garrison twenty tiles from a hero carrying half a
 * million scores nothing and is correctly ignored.
 */
export const COLLECT_BP = 8000;
/** Below this a garrison is a town guard, not an army worth a march. */
export const COLLECT_MIN = 1500;
/**
 * And the ceiling on it, which is the half that stops the errand eating the war.
 *
 * Picking an army up is a *means*. Priced at what the creatures are worth it
 * becomes the end: measured on seed 12161 at day 200, `p3` was looking at an
 * enemy town twelve tiles away that its own probe called a **win**, scoring
 * 74,250 - against a hall seven tiles away holding 277k of its own troops,
 * scoring 965,567. It went home. It went home every day for a hundred days,
 * which is what "the computers circle each other" looks like from inside.
 *
 * An army waiting at home is worth at most what a town is worth, because a town
 * is what it is for. Capped there and walked to with the same patience a town
 * is, the two are compared on distance - fetch it if it is nearer than the
 * front, fight if it is not - and the endgame weights above then put the last
 * town of a dying kingdom above any errand at all.
 */
export const COLLECT_CAP = ENEMY_TOWN_VALUE;
/** A hero we are only remembering has moved since; the memory is a lead, not a
 * target, so it is worth a fraction of the hero we can actually see. */
export const STALE_HERO_BP = 3000;
/** A town we are only remembering has not moved. Nothing is discounted for that. */
export const STALE_TOWN_BP = 9000;
/** Standing in one's own town to collect a week of recruits. */
export const OWN_TOWN_RESTOCK_BP = 5000;
/**
 * Coming home to meet an enemy hero. Priced above an enemy town on purpose - a
 * kingdom that trades its capital for the enemy's has gained nothing and has
 * spent the seven days it now has to find another one - but walked to with the
 * same patience, because noticing a threat in time is the same skill as
 * planning a march and belongs on the same difficulty axis.
 */
/**
 * The most of a town's defence stake that troops already on the road may
 * excuse.
 *
 * Never all of it. A column arrives at the hall and stands in the garrison; it
 * does not sortie, it does not chase, and it cannot retake the town if the
 * timing is off by a day. Six thousand basis points says a relief force worth
 * more than the town buys back at most 60% of the panic, so a hero who is two
 * tiles from a capital still turns round and a hero who is halfway across the
 * map finishes what he is doing.
 */
export const RELIEF_MAX_BP = 6000;

export function reliefAdjusted(stake: number, relief: number): number {
  if (relief <= 0) return stake;
  const excused = Math.min(Math.floor((stake * RELIEF_MAX_BP) / 10000), relief);
  return stake - excused;
}

export const defendPatience = (p: DifficultyProfile): number =>
  Math.max(2, p.conquestPatience);
/**
 * A subterranean gate, valued by the level it opens rather than by itself.
 *
 * The pathfinder is one Dijkstra pass over one z-plane (`computeReachable`
 * takes its level from the origin and never leaves it), so no route the AI can
 * cost crosses a gate and nothing on the far side is ever a target. What the
 * planner can do without a second pathfinder is price the *door*: a gate whose
 * far level is unexplored is worth what that much unexplored ground is worth,
 * which puts the AI through it once and lets the ordinary frontier scoring take
 * over on the other side. When the pathfinder learns to route between levels,
 * this becomes redundant rather than wrong.
 */
export const GATE_LEVEL_VALUE = 6000;
/**
 * How well the level a hero is standing on has to be understood before the door
 * to the other one is worth walking to at all.
 *
 * Priced any higher, or without this gate, the door is the best object on the
 * map for every hero for the whole game: `levelDarkBp` counts a plane's cells,
 * an underground is mostly solid rock, and rock is never "seen", so the far
 * level reads as permanently dark and every hero commutes. Measured on seed
 * 16838 at `GATE_LEVEL_VALUE` 14000: 1212 of 3655 commands were heroes walking
 * to a subterranean gate, `scout` fired 32 times, and neither side ever found
 * the other's capital. Going down when the surface is finished is the right
 * behaviour and this is the rule that says so.
 */
export const GATE_SURFACE_DONE_BP = 2500;
/**
 * How much darker the far level has to be before the door is worth anything.
 *
 * Without a dead band a gate between two half-explored levels is worth a little
 * in whichever direction happens to be one percent darker this turn, and the
 * direction flips as soon as the hero arrives - so the hero paces the gate for
 * the rest of the game. Twenty points of difference is a real "the other side
 * is the unknown half", and it is stable across the handful of cells one visit
 * uncovers.
 */
export const GATE_MIN_GAIN_BP = 2000;

/**
 * What an errand at home is worth to a kingdom that has not found its enemy.
 *
 * `town.collect` is capped at what a town is worth and the hall is one step
 * away, so it scores four hundred thousand and nothing on the map can outbid
 * it. That is right when there is a siege to carry the army to and absurd when
 * there is not: measured on 72x72 seed 32676, `p1`'s only hero spent days 25 to
 * 300 walking out of its capital for a resource pile and straight back in for
 * the garrison, 402 collects and 266 errands, while 84% of the board stayed
 * black and it never saw a rival town.
 *
 * An army waiting at home is a *means*, and while the enemy has not been found
 * there is nothing for it to be a means to. Damped rather than removed, because
 * the hero should still pick the garrison up on his way past - which is what
 * this leaves him doing: he collects once at close range, and the morning after
 * that, standing in the hall with the target suppressed, the frontier wins and
 * he marches. The moment a rival town is known `hunting` goes false and the
 * errand is worth its face value again, which is the sequence the war wants:
 * find them, go home for the army, march.
 */
export const HUNT_ERRAND_BP = 1200;

/**
 * When the army waiting at home stops being an errand and becomes the campaign.
 *
 * `HUNT_ERRAND_BP` above damps a pickup to a tenth while the enemy has still
 * not been found, on the argument that an army at home is a *means* and there
 * is nothing yet for it to be a means to. That argument holds while the hero
 * already carries the kingdom's army and the hall is topping him up. It is
 * exactly wrong when the hall holds the army and the hero is a scout: then the
 * pickup is not an errand, it is the moment this kingdom acquires a field
 * force, and it is what makes the *search* work too - a scout stops at the
 * first guarded road, and the road is what the frontier is behind.
 *
 * Measured on 72x72 seed 24757, the game this rule was written for: at day 200
 * `p1` held **1.45M of army in its garrisons and 20,775 in the hand of its best
 * hero**, unchanged since day 40, with 61% of the map still black and one
 * rival town known and not reachable. It scouted for a hundred and sixty days
 * with a twentieth of its own army because the pickup that would have ended
 * that was priced at a tenth, every morning, for the whole game.
 *
 * Three times, and the multiple is what keeps the exemption narrow: it fires
 * only when the fetch would multiply what the hero carries, so a hall with a
 * week of recruits in it next to a hero carrying an army is still an errand and
 * is still damped. It cannot oscillate - the moment the army is picked up,
 * `waiting` is zero and the damping is back. And it cannot split the kingdom
 * over three halls: `town.collect` is offered to the main hero and to nobody
 * else, which is a rule this one inherits rather than weakens.
 *
 * Measured against the alternative of not having it, everything else in this
 * round held fixed: 48x48 two-player goes from a mean of 53 days to 39 with the
 * same ten games decided and the same two short ones, 48x48 four-player from
 * 109 to 92, and 72x72 two-player is a wash at 58 against 57.
 */
export const MASSING_MULTIPLE = 3;
/** A shrine teaching a spell the army already has, everywhere. */
/**
 * What finding the Grail is worth, in the same gold-equivalents as everything.
 *
 * Read off the content rather than guessed: every faction's Grail structure
 * carries `income.gold +5000`, and a mine is already priced at
 * `MINE_HORIZON_DAYS` of its income, so the Grail is priced at the same horizon
 * of the same currency. The other half of what it gives - half again the weekly
 * growth in one town - is deliberately not counted here, because which town
 * gets it is not known while the shovel is still in the ground.
 *
 * A hundred and twenty-five thousand puts it above `ENEMY_TOWN_VALUE`, which is
 * correct and is why it is discounted below rather than used raw: it is the
 * biggest single prize on the map and also the one that pays out last.
 */
export const GRAIL_INCOME_PER_DAY = 5000;
export const GRAIL_WORTH = GRAIL_INCOME_PER_DAY * MINE_HORIZON_DAYS;

/**
 * How much of `GRAIL_WORTH` the search itself is scored at.
 *
 * A quarter. Standing on every obelisk on the map is a long errand that pays
 * nothing until the last one, the dig costs a whole day of movement on top, and
 * a rival may be three obelisks ahead. Scored at face value the AI would drop a
 * siege to go sightseeing; scored at a quarter it collects obelisks it passes
 * and makes a special trip only for the last few, which is how the search is
 * actually worth running.
 */
export const GRAIL_CHASE_BP = 2500;

export const SHRINE_KNOWN = 300;
/** A shrine the army's Wisdom cannot unlock yet: worth remembering, not a detour. */
export const SHRINE_LOCKED = 700;
export const SHRINE_BASE = 1800;
/** A level-five shrine is worth walking a long way for. */
export const SHRINE_PER_LEVEL = 1400;

/**
 * What this player's heroes can still learn.
 *
 * Memoised per shadow rather than recomputed per object: `objectValue` is called
 * once for every observed object on the map and this is a scan of every hero's
 * spellbook, so doing it inline would be an eight-thousand-entry loop per
 * decision to answer the same question three hundred times.
 */
export interface SpellAppetite {
  /** The highest spell level any hero of ours could learn today. */
  readonly usableLevel: number;
  /** Spells every book-carrying hero already holds. */
  readonly knownByAll: ReadonlySet<string>;
}

const APPETITE = new WeakMap<Shadow, SpellAppetite>();

export function spellAppetite(sh: Shadow): SpellAppetite {
  const hit = APPETITE.get(sh);
  if (hit !== undefined) return hit;
  let usableLevel = 0;
  let knownByAll: Set<string> | null = null;
  for (const h of sh.obs.heroes) {
    const d = h.detail;
    if (d === null || !hasSpellbook(d)) continue;
    const lvl = maxSpellLevel(d);
    if (lvl > usableLevel) usableLevel = lvl;
    const mine = new Set<string>(d.spellbook as readonly string[]);
    if (knownByAll === null) knownByAll = mine;
    else for (const k of [...knownByAll]) if (!mine.has(k)) knownByAll.delete(k);
  }
  const out: SpellAppetite = { usableLevel, knownByAll: knownByAll ?? new Set<string>() };
  APPETITE.set(sh, out);
  return out;
}

/**
 * How dark each level still is, in basis points, computed once per shadow.
 * `objectValue` is called for every observed object and a level scan is 1300
 * cells, so doing it inline would be a per-object sweep of the whole map.
 */
const LEVEL_DARK = new WeakMap<Shadow, readonly number[]>();

export function levelDarkBp(sh: Shadow): readonly number[] {
  const hit = LEVEL_DARK.get(sh);
  if (hit !== undefined) return hit;
  const m = sh.obs.map;
  const plane = m.width * m.height;
  const out: number[] = [];
  for (let z = 0; z < m.levels; z++) {
    let dark = 0;
    const base = z * plane;
    for (let i = 0; i < plane; i++) if (m.visibility[base + i] === 0) dark++;
    out.push(plane === 0 ? 0 : Math.floor((dark * 10000) / plane));
  }
  LEVEL_DARK.set(sh, out);
  return out;
}

export type Shortage = Readonly<Record<ResourceKind, number>>;

const NEUTRAL_SHORTAGE: Shortage = Object.freeze({
  gold: NEUTRAL_BP, wood: NEUTRAL_BP, ore: NEUTRAL_BP, mercury: NEUTRAL_BP,
  sulfur: NEUTRAL_BP, crystal: NEUTRAL_BP, gems: NEUTRAL_BP,
});

/**
 * Which resources the AI is short of, expressed as a multiplier on anything
 * that yields them. The reference is not "what do I have least of" - that
 * favours gems forever - but "what does the next thing I want to build cost
 * that I cannot pay". A shortage is a fact about a plan, not about a purse.
 */
/**
 * The most of each resource any building that is ready to build would ask for.
 * The gap between this and the purse is what a trade has to close.
 */
export function resourceWant(sh: Shadow): Readonly<Record<string, number>> {
  const want: Record<string, number> = {};
  for (const t of sh.obs.towns) {
    if (t.detail === null) continue;
    const built = new Set<string>(t.detail.built as readonly string[]);
    for (const b of sh.content.buildings.values()) {
      if ((b.faction as string) !== (t.detail.faction as string)) continue;
      if (built.has(b.id as string)) continue;
      let ready = true;
      for (const r of b.requires) if (!built.has(r as string)) { ready = false; break; }
      if (!ready) continue;
      for (const r of RESOURCES) {
        const need = (b.cost as Partial<Record<ResourceKind, number>>)[r] ?? 0;
        if (need > (want[r] ?? 0)) want[r] = need;
      }
    }
  }
  return want;
}

export function resourceShortage(sh: Shadow): Shortage {
  const have = sh.obs.resources;
  if (have === null) return NEUTRAL_SHORTAGE;
  const want = resourceWant(sh);
  const out: Record<string, number> = {};
  for (const r of RESOURCES) out[r] = (want[r] ?? 0) > have[r] ? SHORTAGE_BP : NEUTRAL_BP;
  return out as Shortage;
}

// ------------------------------------------------------------------ targets

export interface Target {
  readonly cell: number;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly value: number;
  readonly why: string;
  readonly object: ObjectId | null;
  /** The guard that has to be beaten first, if the content declares one. */
  readonly guard: readonly (ArmySlot | null)[] | null;
  /** Set when there is a fight but the AI cannot know its size. */
  readonly unknownFight: boolean;
  readonly oneVisitPerHero: boolean;
  /**
   * Divisor on the walk, for a target whose worth does not shrink with the
   * distance to it. A chest ten tiles away is worth a tenth of a chest one tile
   * away because the nine days buy nothing; a capital ten tiles away is worth
   * exactly one capital. Patience is how many days of march the AI is willing
   * to treat as one, and it is set by difficulty.
   */
  readonly patience: number;
  /**
   * The army to fight before this is taken, when the real one is behind fog.
   * Built by `threat.ts` out of public facts and settled by the real battle
   * kernel, never by a formula.
   */
  readonly presumed: readonly (ArmySlot | null)[] | null;
  /**
   * Cells a hero may actually stand on to collect this. A mine's own tile is
   * impassable - `layFootprint` blocks the footprint and leaves the entrances
   * open - so targeting the anchor and nothing else would make the AI ignore
   * every mine on the map.
   */
  readonly approach: readonly number[];
  /**
   * Where the march stops when the fight in front of it may not be taken on
   * trust: the ring of tiles around a hall whose flag is under fog. Null for
   * everything else, which is everything the AI can see the size of.
   *
   * A second list rather than a replacement for `approach`, because whether the
   * last step is taken blind is a question about the hero making it - see
   * `blindMargin` - and `collectTargets` is computed once for every hero.
   */
  readonly sighting: readonly number[] | null;
  /**
   * The one hero this target is offered to, or null for "anybody".
   *
   * Force concentration needs somewhere to say "this is for the main army and
   * nobody else". Collecting a garrison is the case: three heroes each fetching
   * a third of the kingdom is three thirds of an army, which is the position
   * this planner was already stuck in. `planAdventure` never sends two heroes
   * to one tile, but that only stops them arriving together - it does not stop
   * them splitting the kingdom over three different halls.
   */
  readonly onlyHero: HeroId | null;
  /**
   * The fortification the fight for this target happens behind: 0 on open
   * ground, 1..3 for a town (`presumedFort`). Handed to the probe, which fights
   * the siege rather than a field battle with the same two armies in it.
   */
  readonly fort: number;
  /**
   * The army standing at the far end of a pickup, and 0 for everything that is
   * not one. `value` cannot answer this: it is capped at what a town is worth
   * (`COLLECT_CAP`) and damped for the hunt, and what a hero needs in order to
   * tell an errand from the moment his kingdom acquires a field army is the
   * uncapped number. See `MASSING_MULTIPLE`.
   *
   * A garrison is measured in `armyStrength`, which weights the top of a
   * ladder; a hall's unbought recruits are measured in flat `aiValue`, because
   * that is what `town.restock` is priced from and they are not an army until
   * they are paid for. The second reads a little low against the first, which
   * is the right direction for a rule that only ever lifts a damping.
   */
  readonly waiting: number;
}

const payNum = (p: MapObjectDef["payload"], k: string, d = 0): number =>
  typeof p[k] === "number" ? (p[k] as number) : d;
const payStr = (p: MapObjectDef["payload"], k: string): string =>
  typeof p[k] === "string" ? (p[k] as string) : "";
const payFlag = (p: MapObjectDef["payload"], k: string): boolean => p[k] === true;

export function guardOf(sh: Shadow, def: MapObjectDef | undefined): readonly (ArmySlot | null)[] | null {
  if (def === undefined) return null;
  const creature = payStr(def.payload, "creature");
  const count = payNum(def.payload, "count");
  if (creature === "" || count <= 0) return null;
  if (!sh.content.creatures.has(creature as CreatureDefId)) return null;
  const slots: (ArmySlot | null)[] = [null, null, null, null, null, null, null];
  slots[0] = { def: creature as CreatureDefId, count };
  return slots;
}

/**
 * What a visitable service is worth to the hero the kingdom would send, or null
 * for a payload that names none. The lead hero stands in for "whoever goes":
 * targets are scored before one is picked, and pricing a Warcraft Keep against an
 * empty army would send nobody to one.
 */
function serviceOf(sh: Shadow, pay: Payload): number | null {
  const id = mainHero(sh);
  if (id === null) return null;
  const h = sh.state.heroes[id];
  if (h === undefined) return null;
  return serviceValue(makeCtx(sh.state, sh.content), h, pay);
}

/** Gold-equivalent of one observed object, before any fight is priced in. */
export function objectValue(sh: Shadow, o: ObservedObject, short: Shortage): number {
  const def = sh.content.mapObjects.get(o.def);
  const pay = def?.payload ?? {};
  const exp = (n: number): number => Math.floor((n * EXPERIENCE_TO_GOLD_BP) / 10000);
  const res = (kind: string, amount: number): number =>
    Math.floor(
      (amount * (sh.content.resourceValue[kind as ResourceKind] ?? 1)
        * (short[kind as ResourceKind] ?? NEUTRAL_BP)) / 10000,
    );

  switch (o.kind) {
    case "resource": {
      const kind = payStr(pay, "resource");
      const amount = payNum(pay, "amount", Math.floor((payNum(pay, "min") + payNum(pay, "max")) / 2));
      return kind === "" ? 500 : res(kind, amount);
    }
    case "mine": {
      if (o.owner === sh.player) return 0;
      const kind = payStr(pay, "resource");
      const amount = payNum(pay, "amount");
      if (kind === "" || amount <= 0) return 3000;
      return res(kind, amount * MINE_HORIZON_DAYS);
    }
    case "dwelling": {
      if (o.owner === sh.player) return 0;
      const c = sh.content.creatures.get(payStr(pay, "creature") as CreatureDefId);
      const growth = payNum(pay, "growth", 1);
      return c === undefined ? 2500 : c.aiValue * growth;
    }
    case "artifact":
      return sh.content.artifacts.get(payStr(pay, "artifact") as ArtifactDefId)?.cost ?? 4000;
    case "treasure":
      return payNum(pay, "gold") + exp(payNum(pay, "experience"));
    case "bank": {
      let v = payNum(pay, "gold") + exp(payNum(pay, "experience"));
      const art = payStr(pay, "artifact");
      if (art !== "") v += sh.content.artifacts.get(art as ArtifactDefId)?.cost ?? 0;
      const kind = payStr(pay, "resource");
      if (kind !== "") v += res(kind, payNum(pay, "amount"));
      const rc = sh.content.creatures.get(payStr(pay, "rewardCreature") as CreatureDefId);
      if (rc !== undefined) v += rc.aiValue * payNum(pay, "rewardCount");
      return v;
    }
    case "monster": {
      // A wandering monster is not loot; it is experience and a tile unblocked.
      // The observation carries the instance stack: the sprite def is a
      // stand-in and its payload is the wrong fight.
      const id = (o.creature ?? payStr(pay, "creature")) as CreatureDefId;
      const n = o.creatureCount ?? payNum(pay, "count");
      const c = sh.content.creatures.get(id);
      return c === undefined ? 0 : exp(c.aiValue * n);
    }
    case "shrine": {
      const spell = payStr(pay, "spell") as SpellDefId;
      const def = sh.content.spells.get(spell);
      if (def === undefined) return 500;
      const app = spellAppetite(sh);
      // A shrine is a spell, and a spell every hero already knows is a walk for
      // nothing. One the army cannot learn yet is worth going back for once
      // Wisdom arrives, but not worth a detour today.
      if (app.knownByAll.has(spell as string)) return SHRINE_KNOWN;
      if (def.level > app.usableLevel) return SHRINE_LOCKED;
      return SHRINE_BASE + def.level * SHRINE_PER_LEVEL;
    }
    case "scholar": return 2500;
    case "bonus": {
      // An obelisk before anything else: it is a `bonus` object with an empty
      // payload, so without this it falls through to the `v === 0` floor and is
      // worth 600 - which is noise, and is why no computer ever ran the Grail
      // search. See `obeliskValue`.
      if (payFlag(pay, "obelisk") || payNum(pay, "obelisk", 0) > 0) {
        return obeliskValue(sh, o);
      }
      let v = payNum(pay, "gold") + exp(payNum(pay, "experience"));
      for (const k of ["attack", "defense", "spellPower", "knowledge"]) v += payNum(pay, k) * 3000;
      v += payNum(pay, "morale") * 800 + payNum(pay, "luck") * 800;
      v += Math.floor(payNum(pay, "movement") / 2);
      v += payNum(pay, "mana") * 40;
      // A visitable *service* - a Warcraft Keep, a Elder Oak, a University,
      // a Black Market - is priced by the kernel rather than here, because the
      // price is a fact about the rule: a University is worth a skill only
      // while the hero has one of its four left to learn, and `skillLevel` is
      // what decides that. A second copy of those conditions in the planner is
      // a second statement of the rules that can drift from the first.
      //
      // Priced for the kingdom's best hero, which is the one who will be sent:
      // `objectValue` scores a target before a hero is chosen for it.
      const service = serviceOf(sh, pay);
      if (service !== null) v += service;
      if (v === 0) v = 600;
      return v;
    }
    case "portal": {
      // Worth the ground on the *far* side that this side does not already
      // have. A gate priced by the far level alone is a gate worth using in
      // both directions at once: the hero steps through, the level he left is
      // now the dark one, and he steps straight back - measured, at 2315
      // teleports in one 200-day game. The difference is zero for a pair of
      // equally explored levels and negative going the wrong way, so a hero
      // goes down when the underground is the unknown half and stays there.
      const dark = levelDarkBp(sh);
      if (dark.length < 2) return 0;
      const here = dark[o.z] ?? 0;
      if (here > GATE_SURFACE_DONE_BP) return 0;
      let best = 0;
      for (let z = 0; z < dark.length; z++) {
        if (z === o.z) continue;
        const gain = (dark[z] ?? 0) - here;
        if (gain > best) best = gain;
      }
      if (best < GATE_MIN_GAIN_BP) return 0;
      // Squared, so the door is worth walking to for the first trip and worth
      // less and less as the far level fills in. Linear decay never falls far
      // enough: an underground is mostly solid rock, rock is never "seen", and
      // a level that reads as sixty per cent dark for ever is a level every
      // hero keeps commuting to.
      return Math.floor((GATE_LEVEL_VALUE * best * best) / 100000000);
    }
    default:
      // garrison, keymaster, gate, sign, boat, town, hero, obstacle: either
      // handled elsewhere or worth nothing to walk onto.
      return 0;
  }
}

/**
 * What taking this is worth, before the fight in front of it is priced.
 *
 * The difficulty term is the one the aggression rule lost, and losing it is why
 * an easy computer marched exactly as hard as an impossible one. It belongs
 * here rather than in the fight assessment: how much a computer *wants* an
 * enemy capital is a matter of how well it plays, and how likely it is to win
 * the battle in front of it is a matter of fact.
 */
function conquestValue(sh: Shadow, e: KnownEnemy, p: DifficultyProfile): number {
  const base = e.kind === "town" ? ENEMY_TOWN_VALUE : ENEMY_HERO_VALUE + e.rank * 700;
  const staleBp = e.fresh ? 10000 : (e.kind === "town" ? STALE_TOWN_BP : STALE_HERO_BP);
  const flat = Math.floor((base * p.aggressionBp * staleBp) / 100000000);
  return Math.floor((flat * endgameBp(sh, e)) / 10000);
}

/**
 * How close taking this puts the war to over, in basis points of an ordinary
 * town. Ten thousand for anything that is not somebody's last stand.
 */
export function endgameBp(sh: Shadow, e: KnownEnemy): number {
  if (e.kind !== "town") return 10000;
  const weigh = (towns: number, heroes: number): number => {
    const bp = towns <= 1 ? LAST_TOWN_BP : towns === 2 ? SECOND_LAST_TOWN_BP : 10000;
    return heroes === 0 ? Math.floor((bp * NO_HERO_BP) / 10000) : bp;
  };
  // A town whose flag the fog is hiding still belongs to somebody, and the
  // scoreboard names everybody. The cautious reading is the smallest weight any
  // live rival would earn - which is 10000 as long as one of them is still
  // comfortable, and rises only once every one of them is nearly finished.
  if (e.owner === null) {
    let bp = -1;
    for (const o of sh.obs.opponents) {
      if (o.defeated) continue;
      const w = weigh(o.townCount, o.heroCount);
      if (bp < 0 || w < bp) bp = w;
    }
    return bp < 0 ? 10000 : bp;
  }
  for (const o of sh.obs.opponents) {
    if ((o.id as string) !== (e.owner as string) || o.defeated) continue;
    return weigh(o.townCount, o.heroCount);
  }
  return 10000;
}

/**
 * How far ahead of a remembered garrison a hero has to be before he is allowed
 * to walk into it without looking.
 *
 * A siege is decided by the step that starts it, and `multiDay` lets that step
 * be the twenty-eighth of a path laid days earlier - so the fight the hero
 * walks into is priced from whatever the planner believed when the route was
 * drawn, and nothing re-asks the question on the way in.
 *
 * That is survivable while the estimate is stable, and `presumedArmy` is not:
 * it prices an unowned hall off the hall itself only while the observation is
 * lighting it up, and off the rival-kingdom yardstick otherwise. Measured on
 * 36x36 seed 16838, the same hero, the same army and the same town, one command
 * apart:
 *
 *   cell 213  rank 5  owner null   lit    presumed 5008   verdict lose
 *   cell 213  rank 5  owner null   unlit  presumed 1690   verdict win
 *
 * The garrison the map actually held was 8284. `p1` walked up, looked,
 * declined, stepped back out of sight, forgot, and marched in with 2401 - and
 * the day after, its second hero picked the capital's own garrison up and did
 * exactly the same thing. Two heroes and the hall's defence, spent on one
 * neutral town in two days, and the rival walked into the empty capital on day
 * three. Both of the 36x36 seeds opened by hand while chasing the regression -
 * 16838 and 32676 - were that same shape.
 *
 * So a hero who is not far enough ahead of what he *remembers* stops one tile
 * short, which is where the observation lights the hall up and the honest
 * verdict replaces the remembered one. It is not a range limit and not a delay:
 * the target keeps its value and its patience, the march is the same march, and
 * the only thing that changes is that the last step is taken with the hall in
 * sight.
 *
 * The margin is what keeps this off the endgame, and its reach is narrower than
 * it reads. A hero three times the size of the garrison he remembers has
 * nothing to learn by looking - the remembered reading would have to be out by
 * a factor of three to matter. But `presumedArmy` prices an unlit hall off
 * `presumedKingdomStrength`, and for a rival nothing is known about that is a
 * *parity* read of our own kingdom, so growing the hero grows the estimate with
 * him: measured on a two-capital fixture, 10 to 800 Spearwardens moves the ratio
 * from 0.52 only as far as 2.38 and never reaches three. The margin therefore
 * cannot be bought with army. It opens exactly where it should - against a
 * rival the public scoreboard already shows beaten down to a town or two,
 * whose presumed hall stops tracking us - which is the endgame, and it is worth
 * a decided game there: measured on the 48x48 four-player seed-4242 set at an
 * earlier point in this round's tuning, the same build with the margin and
 * without it came in at 7 of 10 and 6 of 10.
 */
export const BLIND_MARGIN_BP = 30000;

/**
 * Whether this hero has to look before he commits, given what he remembers.
 *
 * A remembered garrison of nothing is not a reason to be careful - there is no
 * estimate to be wrong about - so it walks in.
 */
export const looksFirst = (ours: number, remembered: number): boolean =>
  remembered > 0 && ours * 10000 < BLIND_MARGIN_BP * remembered;

/** The ring of tiles around a town, for a march that has to stop short. */
function siegeApproach(
  e: KnownEnemy,
  inMap: (x: number, y: number) => boolean,
  cellOf: (x: number, y: number, z: number) => number,
): number[] | null {
  // A hero is not a town: he has moved since he was seen, so the tile he was on
  // is a lead rather than a door, and walking onto it costs nothing.
  if (e.kind !== "town" || e.fresh) return null;
  const ring: number[] = [];
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue;
      const x = e.x + dx, y = e.y + dy;
      if (inMap(x, y)) ring.push(cellOf(x, y, e.z));
    }
  }
  // A hall wedged into the map edge with no neighbour on the board is still a
  // target; refusing to walk to it at all would be the range limit this is not.
  return ring.length === 0 ? null : ring;
}

/** What the remembered defender of a target is worth, in the AI's own currency. */
function presumedWorth(sh: Shadow, t: Target): number {
  let n = 0;
  for (const s of t.presumed ?? []) {
    if (s === null) continue;
    n += (sh.content.creatures.get(s.def)?.aiValue ?? 0) * s.count;
  }
  return n;
}

/**
 * What one unread obelisk is worth: the whole search, split over what is left
 * of it.
 *
 * `share / remaining` rather than `share / total`, so the value RISES as the
 * set fills in - eight to go and each is a minor detour, one to go and it is
 * worth marching across the map for, because that one hands over the cell. That
 * shape is the search itself: nothing pays until the last piece, so the last
 * piece is where the value has to live.
 *
 * Zero for one this player has already stood on (it is not consumed and not
 * removed, so it looks identical to an unread one - `ObservedObject.marked` is
 * the only thing that tells them apart), and zero once the puzzle is solved,
 * when the remaining obelisks are scenery.
 */
export function obeliskValue(sh: Shadow, o: ObservedObject): number {
  if (o.marked) return 0;
  const pz = sh.obs.puzzle;
  // No registry behind the observation, so no puzzle was reported: fall back to
  // the old floor rather than to zero. An obelisk is still a landmark.
  if (pz === null) return 600;
  const remaining = pz.total - pz.seen;
  if (remaining <= 0) return 0;
  const share = Math.floor((GRAIL_WORTH * GRAIL_CHASE_BP) / 10000);
  return Math.floor(share / remaining);
}

/** The hero of ours carrying the Grail, or null. */
export function grailBearer(sh: Shadow): HeroId | null {
  for (const h of sh.obs.heroes) {
    if (h.owner !== sh.player || h.detail === null) continue;
    if (carriesGrail(h.detail)) return h.id;
  }
  return null;
}

/**
 * Everything worth walking to, computed once per decision and shared by every
 * hero. Enemy assets come from `Shadow.enemies`, which is the only channel that
 * carries them and is itself built from the observation alone.
 */
export function collectTargets(
  sh: Shadow, short: Shortage, p: DifficultyProfile,
): Target[] {
  const out: Target[] = [];
  const m = sh.obs.map;
  const cellOf = (x: number, y: number, z: number): number =>
    z * m.width * m.height + y * m.width + x;

  const inMap = (x: number, y: number): boolean =>
    x >= 0 && y >= 0 && x < m.width && y < m.height;

  for (const o of sh.objects) {
    const value = objectValue(sh, o, short);
    if (value <= 0) continue;
    const def = sh.content.mapObjects.get(o.def);
    const approach: number[] = [];
    const add = (x: number, y: number): void => {
      if (inMap(x, y)) approach.push(cellOf(x, y, o.z));
    };
    add(o.x, o.y);
    for (const [dx, dy] of def?.entrances ?? []) add(o.x + dx, o.y + dy);
    // Last resort: stand next to it and use VISIT_OBJECT. Adjacency is the
    // kernel's own rule for a visit, so this is never a wasted walk.
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        add(o.x + dx, o.y + dy);
      }
    }
    out.push({
      cell: cellOf(o.x, o.y, o.z), x: o.x, y: o.y, z: o.z,
      value, why: `obj.${o.kind}`, object: o.id,
      guard: guardOf(sh, def),
      unknownFight: false,
      sighting: null,
      oneVisitPerHero: def?.oneVisitPerHero ?? false,
      patience: 1,
      presumed: null,
      approach,
      onlyHero: null,
      fort: 0,
      waiting: 0,
    });
  }

  // The war. An enemy town is the only object on the map that ends the game, so
  // it is the only one scored as if the walk to it were free - see `patience`.
  for (const e of sh.enemies) {
    if (e.owner === sh.player) continue;
    out.push({
      cell: e.cell, x: e.x, y: e.y, z: e.z,
      value: conquestValue(sh, e, p),
      why: `enemy.${e.kind}`,
      object: null,
      guard: null,
      unknownFight: true,
      oneVisitPerHero: false,
      // A hero runs away and a town does not, so chasing one is worth less
      // patience than besieging the other.
      patience: e.kind === "town" ? p.conquestPatience : Math.max(1, p.conquestPatience >> 1),
      presumed: presumedArmy(sh, e),
      approach: [e.cell],
      sighting: siegeApproach(e, inMap, cellOf),
      onlyHero: null,
      fort: presumedFort(sh, e),
      waiting: 0,
    });
  }

  // The dig. Two targets and never both: get to the cell, then get home.
  //
  // Both are `patience`-heavy for the same reason a capital is - the Grail does
  // not become less valuable because it is far away, and the walk to it buys
  // the whole prize rather than a share of it.
  const bearer = grailBearer(sh);
  const pz = sh.obs.puzzle;
  if (bearer !== null) {
    // Carrying it. The only thing left worth doing with it is standing in one
    // of our own halls, where `BUILD` becomes legal and the town planner takes
    // over. Offered to the bearer alone: nobody else can deliver it.
    for (const t of sh.obs.towns) {
      const d = t.detail;
      if (d === null || t.owner !== sh.player) continue;
      out.push({
        cell: cellOf(d.x, d.y, d.z), x: d.x, y: d.y, z: d.z,
        value: GRAIL_WORTH, why: "grail.deliver", object: null, guard: null,
        unknownFight: false, oneVisitPerHero: false, sighting: null,
        patience: p.conquestPatience, presumed: null,
        approach: [cellOf(d.x, d.y, d.z)],
        onlyHero: bearer,
        fort: 0,
        waiting: 0,
      });
    }
  } else if (pz !== null && pz.cell >= 0 && inMap(pz.x, pz.y)) {
    // Solved and nobody is carrying it, so the cell is worth standing on. The
    // dig itself is a separate command (`planDig`) because it costs a whole
    // day's movement and is therefore only ever issued on the morning after
    // the walk that arrives.
    out.push({
      cell: pz.cell, x: pz.x, y: pz.y, z: 0,
      value: GRAIL_WORTH, why: "grail.dig", object: null, guard: null,
      unknownFight: false, oneVisitPerHero: false, sighting: null,
      patience: p.conquestPatience, presumed: null,
      approach: [pz.cell],
      onlyHero: null,
      fort: 0,
      waiting: 0,
    });
  }

  // Home. A town with an enemy hero in sight of it outranks the war, because
  // the war is lost by giving away towns faster than they are taken.
  for (const t of threatenedTowns(sh, p)) {
    if (!t.undefended) continue;
    const town = sh.state.towns[t.town];
    if (town === undefined) continue;
    out.push({
      cell: cellOf(town.x, town.y, town.z), x: town.x, y: town.y, z: town.z,
      value: reliefAdjusted(t.stake, t.relief), why: "town.defend", object: null, guard: null,
      unknownFight: false, oneVisitPerHero: false, sighting: null,
      patience: defendPatience(p), presumed: null,
      approach: [cellOf(town.x, town.y, town.z)],
      onlyHero: null,
      fort: 0,
      waiting: 0,
    });
  }

  // The army the kingdom already owns and has not picked up. See `COLLECT_BP`;
  // this is the target that turns a hoard into a siege stack.
  const main = mainHero(sh);
  if (main !== null) {
    for (const t of sh.obs.towns) {
      const d = t.detail;
      if (d === null) continue;
      // Already standing in it: `town.ts` hands the garrison over for free, and
      // a target on the tile the hero is on would be a walk of length zero that
      // consumes his whole turn.
      if (d.visitingHero === main) continue;
      // Only an army the kingdom owns can be handed over; see
      // `recruitsCanBeCollected` in `town.ts` for why a captured hall's is not.
      // Walking to one that cannot be picked up is a walk for nothing.
      if ((sh.state.armies[d.garrison]?.owner ?? null) !== sh.player) continue;
      const held = armyStrength(sh.content, t.garrison);
      if (held < COLLECT_MIN) continue;
      out.push({
        cell: cellOf(t.x, t.y, t.z), x: t.x, y: t.y, z: t.z,
        value: Math.min(COLLECT_CAP, Math.floor((held * COLLECT_BP) / 10000)),
        why: "town.collect", object: null, guard: null,
        unknownFight: false, oneVisitPerHero: false, sighting: null,
        patience: p.conquestPatience, presumed: null,
        approach: [cellOf(t.x, t.y, t.z)],
        onlyHero: main,
        fort: 0,
        waiting: held,
      });
    }
  }

  for (const t of sh.obs.towns) {
    if (t.detail === null) continue;
    let waiting = 0;
    for (const k of Object.keys(t.detail.available).sort()) {
      const n = t.detail.available[k] ?? 0;
      const c = sh.content.creatures.get(k as CreatureDefId);
      if (c !== undefined && n > 0) waiting += c.aiValue * n;
    }
    if (waiting <= 0) continue;
    out.push({
      cell: cellOf(t.x, t.y, t.z), x: t.x, y: t.y, z: t.z,
      value: Math.floor((waiting * OWN_TOWN_RESTOCK_BP) / 10000),
      why: "town.restock", object: null, guard: null,
      unknownFight: false, oneVisitPerHero: false, sighting: null,
      patience: 1, presumed: null,
      approach: [cellOf(t.x, t.y, t.z)],
      onlyHero: null,
      fort: 0,
      waiting,
    });
  }

  out.sort((a, b) => (b.value - a.value) || (a.cell - b.cell));
  return out;
}

/**
 * The edge of the fog, one representative cell per block of `FRONTIER_BLOCK`.
 *
 * Every explored cell beside unexplored ground is a frontier cell, and on a
 * half-lit 36x36 map that is several hundred of them, all in a handful of
 * places. Scoring every one of them per hero per command is the reason the old
 * scorer could only afford to look at frontier cells the hero could reach
 * *today* - and that restriction is what stopped the AI ever crossing the map:
 * once the local edge is walked out, no explorable cell is one day away, the
 * explore term goes silent and the computer settles down to a lifetime of
 * shrine visits without ever finding the enemy. Measured, on seed 24757: five
 * `explore` commands in four hundred turns, and neither player ever saw the
 * other's capital.
 *
 * One cell per block keeps the list bounded and spatially spread, so the AI can
 * afford to consider a frontier six days away - which is what it takes to walk
 * into the other half of a map.
 */
export const FRONTIER_BLOCK = 4;

export function frontier(sh: Shadow): { cell: number; unseen: number }[] {
  const m = sh.obs.map;
  const vis = m.visibility;
  const cost = m.moveCost;
  const w = m.width, h = m.height;
  const blocksX = Math.ceil(w / FRONTIER_BLOCK);
  const best = new Map<number, { cell: number; unseen: number }>();
  for (let z = 0; z < m.levels; z++) {
    const base = z * w * h;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = base + y * w + x;
        if (vis[i] === 0 || cost[i] === 0) continue;
        let unseen = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nx = x + dx, ny = y + dy;
            if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
            if (vis[base + ny * w + nx] === 0) unseen++;
          }
        }
        if (unseen === 0) continue;
        const block = z * blocksX * blocksX
          + Math.trunc(y / FRONTIER_BLOCK) * blocksX + Math.trunc(x / FRONTIER_BLOCK);
        const held = best.get(block);
        // Ties keep the lower cell index, so the list is a function of the fog
        // and not of the scan order that produced it.
        if (held === undefined || unseen > held.unseen) best.set(block, { cell: i, unseen });
      }
    }
  }
  const out = [...best.values()];
  out.sort((a, b) => (b.unseen - a.unseen) || (a.cell - b.cell));
  return out;
}

// ------------------------------------------------------------------ fights

/**
 * The probe allowance for one *turn*, not one decision.
 *
 * A probe is a whole battle run through the real kernel, so it is the most
 * expensive thing the AI does by two orders of magnitude. Budgeting it per
 * command would let a single turn spend the allowance forty times over and -
 * worse - throw the answer away between commands and re-fight the same guard.
 * The cache is keyed on the two army compositions, so it stays valid exactly
 * until one of them changes, which is when the answer stops being true.
 */
export interface FightBudget {
  probesLeft: number;
  readonly cache: Map<string, FightVerdict>;
}

export function newFightBudget(p: DifficultyProfile): FightBudget {
  return { probesLeft: p.battleProbes, cache: new Map<string, FightVerdict>() };
}

function armyKey(slots: readonly (ArmySlot | null)[]): string {
  const parts: string[] = [];
  for (const s of slots) parts.push(s === null ? "-" : `${s.def}x${s.count}`);
  return parts.join("|");
}

/** FNV-1a over the matchup key: a probe's seed must not drift between calls. */
function seedFor(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function heroInput(h: HeroState): Parameters<typeof probeArmies>[0]["hero"] {
  return {
    id: h.id, owner: h.owner,
    attack: h.primary.attack, defense: h.primary.defense,
    spellPower: h.primary.spellPower, knowledge: h.primary.knowledge,
    mana: h.mana, spellbook: h.spellbook, army: h.army,
  };
}

/**
 * Can this hero take this guard? Answered by running the real battle kernel
 * once, inside a probe budget - and by a strength ratio when the budget is
 * spent or the difficulty does not buy probes at all.
 *
 * `fort` is the wall the fight happens behind, 0 for the open ground a
 * wandering monster stands on and 1..3 for a town (`presumedFort`). It is part
 * of the cache key because the same two armies give different answers on either
 * side of a curtain wall - which is the whole reason the probe is told about
 * it - and a cache that could not tell them apart would answer a siege with
 * yesterday's field battle.
 *
 * The ratio fallback deliberately ignores it. That arm exists for a difficulty
 * that buys no probes at all, where the whole estimate is one multiplication;
 * folding a wall into it would be the closed-form siege estimator this file
 * exists to not have.
 */
export function assessGuard(
  sh: Shadow, p: DifficultyProfile, budget: FightBudget,
  hero: HeroState, guard: readonly (ArmySlot | null)[], terrain: TerrainKind,
  fort = 0,
): FightVerdict {
  const mine = sh.state.armies[hero.army];
  if (mine === undefined) return "lose";
  const key = `${armyKey(mine.slots)}#${armyKey(guard)}#f${String(fort)}`;
  const hit = budget.cache.get(key);
  if (hit !== undefined) return hit;

  let verdict: FightVerdict;
  if (budget.probesLeft > 0) {
    budget.probesLeft--;
    const r = probeArmies(
      { slots: mine.slots, hero: heroInput(hero) },
      { slots: guard, hero: NO_HERO },
      terrain,
      sh.content,
      // Deterministic, and a function of the two armies rather than of the
      // command counter, so re-asking the same question gives the same answer.
      seedFor(key),
      { maxRounds: p.probeRounds, maxCommands: p.probeCommands, profile: p },
      fort,
    );
    verdict = verdictOf(r);
  } else {
    const ours = armyStrength(sh.content, mine);
    let theirs = 0;
    for (const s of guard) {
      if (s === null) continue;
      theirs += (sh.content.creatures.get(s.def)?.aiValue ?? 0) * s.count;
    }
    verdict = theirs <= 0 ? "win"
      : ours * 1000 >= p.attackRatioFp * theirs ? "win"
        : ours * 1000 >= (p.attackRatioFp * theirs) / 2 ? "costly" : "lose";
  }
  budget.cache.set(key, verdict);
  return verdict;
}

/**
 * A fight whose size the AI cannot see: an enemy hero, or an enemy town.
 *
 * Two questions, in this order, and the order is the whole design.
 *
 * **Is this the hero we would send?** Force concentration is a rule about who
 * marches, not about how hard the fight is, so it is settled first and settled
 * without a battle: a hero carrying less than `massBp` of the best army the
 * kingdom has is a scout, and a scout thrown at a fort is an army given away one
 * seventh at a time. Measured against the best army rather than against the sum
 * of every army, because with four heroes and a garrison no hero ever holds a
 * majority of the sum and a threshold read that way declines every march for
 * ever. The threshold is a difficulty knob because massing is a skill; an easy
 * computer sends whoever is nearest.
 *
 * **Do we win it?** Answered by fighting it, against the plausible defender
 * `threat.ts` assembles out of the town's faction, the public scoreboard and
 * what was last seen through the fog - so the same `assessGuard` that prices a
 * wandering monster prices a siege, on the same probe budget, through the same
 * kernel. There is no second estimator and no second safety margin:
 * `attackRatioFp` is the one, and it is already per difficulty.
 *
 * The rule this replaces compared the hero's army against `strongestOwnArmy`,
 * which is a maximum over our own heroes - so for the hero carrying the main
 * army the test read `ours >= 1.4 * ours`, every enemy asset was filed "lose",
 * and two AIs could play two hundred days and decide nothing. Its successor
 * dropped the ratio altogether to un-break it, which un-broke the difficulty
 * term with it. This has both.
 */
function assessConquest(
  sh: Shadow, p: DifficultyProfile, budget: FightBudget,
  hero: HeroState, t: Target, terrain: TerrainKind,
): FightVerdict {
  const mine = sh.state.armies[hero.army];
  const ours = armyStrength(sh.content, mine ?? null);
  if (ours <= 0) return "lose";

  const best = strongestOwnArmy(sh);
  if (best > 0 && ours * 10000 < p.massBp * best) return "lose";

  const verdict = t.presumed !== null
    ? assessGuard(sh, p, budget, hero, t.presumed, terrain, t.fort)
    // Nothing to assemble a defender out of - a hero of an unknown faction on a
    // map whose content has no ladder for it. Fall back to the old proxy, which
    // at least keeps the weakest hero at home.
    : strongestOwnArmy(sh) <= 0
      ? "unknown"
      : ours >= strongestOwnArmy(sh) ? "costly" : "lose";

  return stakeAllows(sh, hero.id, ours, verdict) ? verdict : "lose";
}

/**
 * What the kingdom still fields the morning after this hero is destroyed: the
 * strongest single army left standing in it, halls included.
 *
 * A garrison counts because it is what a hall is defended by, and a hall is
 * what the war is decided on. Strongest single rather than the sum: three
 * scouts of two hundred are not an army, and the question this answers is
 * whether anything left could meet what just beat him.
 */
export function reserveBehind(sh: Shadow, hero: HeroId): number {
  let best = 0;
  for (const h of sh.obs.heroes) {
    if (h.owner !== sh.obs.viewer || h.id === hero) continue;
    const v = armyStrength(sh.content, h.army);
    if (v > best) best = v;
  }
  for (const t of sh.obs.towns) {
    if (t.detail === null) continue;
    const v = armyStrength(sh.content, t.garrison);
    if (v > best) best = v;
  }
  return best;
}

/**
 * "Costly" is the AI's own word for a fight it wins and is gutted by, and
 * whether that is a price or a catastrophe is a question about the kingdom
 * behind the hero rather than about the odds in front of him.
 *
 * A kingdom that has a second army, or a hall defended by one, can spend a
 * gutted hero and go on playing. A kingdom whose whole field force is the stack
 * in this one hand cannot: the hero comes out of a won siege with a seventh of
 * what he went in with, the rival's next hero walks into the capital, and the
 * war is over on a fight the AI itself priced as a coin-flip. Measured on 36x36
 * seed 16838 at day 3: `p1`'s surviving hero collected the capital's garrison
 * (5247 down to 2970, hero 2252 up to 4364), marched on a neutral hall the
 * probe read at 5008 and the map actually held 8284, and died - and the capital
 * it had just emptied fell that afternoon to a hero carrying 3414.
 *
 * So while the kingdom is all-in on one stack, "costly" is a refusal. It is not
 * a reserve and not a floor - nothing is held back and no hall is forbidden to
 * hand its troops over. The bet is simply declined until there is something
 * behind it, which is a condition the kingdom grows out of on its own: a second
 * hero with a week of recruits, or a hall that kept enough to be worth
 * comparing, and the fight is affordable again. Round 10 measured and rejected
 * the two shapes this is not - a home-guard hero, which costs a hero for ever,
 * and an absolute garrison floor, which took the 48x48 headline from 8 of 10 to
 * 3 - and neither says anything about this one, because neither of them was
 * about the stake.
 *
 * "win" and "lose" are untouched. A hero who is sure of a fight takes it with
 * everything he has, which is exactly what the endgame is made of.
 */
export const ALL_IN_BP = 10000;

export function stakeAllows(
  sh: Shadow, hero: HeroId, ours: number, verdict: FightVerdict,
): boolean {
  if (verdict !== "costly") return true;
  return reserveBehind(sh, hero) * 10000 >= ALL_IN_BP * ours;
}

// -------------------------------------------------------------------- routes

/**
 * The route table this planner scores against, which is not always the
 * enumerator's.
 *
 * `enumerateLegalCommands` runs one `computeReachable` per hero and hands it
 * over in `LegalCommand.reachable`. That pass is given no portal links, so it
 * settles one z-plane and treats a gate mouth as ordinary ground - which is
 * two wrong answers at once: everything on the far side is unreachable, and a
 * route drawn *across* a mouth is a route the hero never finishes, because
 * stepping onto it teleports him and ends his day.
 *
 * Once this kingdom has been through a gate (`portals.ts`) the graph it can
 * walk is bigger than the enumerator's, so the same function is re-run with the
 * links it has learned. Re-run and not adjusted: there is one notion of
 * distance in this codebase and a second one here would be the same mistake as
 * a closed-form battle estimator. It costs nothing until a link exists, which
 * is most of most games.
 *
 * `explored` is handed over even though the shadow's `moveCost` is already zero
 * outside explored ground. Belt and braces, and I9 is worth stating in the call
 * rather than inheriting from what happens to be in a plane.
 */
const LINKED_SETS = new WeakMap<Shadow, Map<string, ReachableSet>>();

export function routeSet(
  sh: Shadow, heroId: HeroId, enumerated: ReachableSet,
): ReachableSet {
  if (sh.links.length === 0) return enumerated;
  let byHero = LINKED_SETS.get(sh);
  if (byHero === undefined) { byHero = new Map<string, ReachableSet>(); LINKED_SETS.set(sh, byHero); }
  const hit = byHero.get(heroId as string);
  if (hit !== undefined) return hit;
  const hero = sh.state.heroes[heroId];
  if (hero === undefined) return enumerated;
  const set = computeReachable(
    sh.state.map, enumerated.origin, moveRulesFor(makeCtx(sh.state, sh.content), hero),
    { links: sh.links, explored: sh.obs.map.visibility },
  );
  byHero.set(heroId as string, set);
  return set;
}

/**
 * A town held by a rival who is still in the game.
 *
 * The distinction this draws is the one the planner had lost. `knownEnemies`
 * publishes every town that is not ours, and a hall that belongs to nobody is
 * published with `owner: null` exactly like a rival capital the fog has never
 * lit. Treating the two the same is what told a computer it had found the war
 * when all it had found was an empty fort.
 *
 * `owner` is read off the observation, which names an owner only for a town it
 * is lighting now or remembers lit, so this is knowledge the player has rather
 * than a peek at the board.
 */
function rivalHeld(sh: Shadow, e: KnownEnemy): boolean {
  if (e.kind !== "town" || e.owner === null || e.owner === sh.player) return false;
  for (const o of sh.obs.opponents) if (o.id === e.owner) return !o.defeated;
  return false;
}

/**
 * Has this kingdom found the war at all?
 *
 * "I know of a town a live rival holds." Not "I know of a town", which is what
 * the planner used to ask and is the question a neutral hall answers yes to.
 */
export function warFound(sh: Shadow): boolean {
  for (const e of sh.enemies) if (rivalHeld(sh, e)) return true;
  return false;
}

// --------------------------------------------------------------------- plan

interface Scored {
  readonly hero: HeroId;
  readonly target: number;
  readonly score: number;
  readonly why: string;
  readonly set: ReachableSet;
}

/**
 * Cells a hero's walk ends on whether the planner meant it to or not.
 *
 * `objects.ts` returns `STOP` for a portal - "movement ends on arrival, as in
 * The classic" - so every step a path has after a subterranean gate is a step the
 * kernel was never going to take. Worse than wasted: the exit prompt carries the
 * unwalked remainder in its resume token, and answering it with no movement left
 * is refused with `INSUFFICIENT_MOVEMENT`, which turns a legal enumerated
 * response into a rejected command. Ending the path on the gate is both the
 * honest plan and the one that cannot bounce.
 */
const STOPS_MOVEMENT = new WeakMap<Shadow, ReadonlySet<number>>();

/**
 * Cells that start a fight the planner did not ask for.
 *
 * The pathfinder costs a wandering monster's tile like any other, so a route to
 * anywhere can run straight through one - and stepping on it raises
 * `FIGHT_OR_FLEE`, which `planPrompt` answers on the merits of the fight rather
 * than on the merits of the walk. Decline it and the hero has not moved; plan
 * again tomorrow and the same route is still the best one, so the same monster
 * raises the same prompt and the hero is frozen where he stands for the rest of
 * the game.
 *
 * Measured on 36x36 seed 1000 at Easy: `hero:4` stood at (22,15) with 2013
 * movement from day ten to day forty, issuing the identical seventeen-step walk
 * to its own hall every single morning, refusing `object:188` (threat 2430)
 * every single morning, and never taking a step. Its kingdom held one town, its
 * rival held one town and no heroes at all, and the game could not end.
 *
 * So a walk may not *begin* on a fight this hero would refuse, and the test is
 * `assessGuard` - the same probe through the same real battle kernel on the
 * same per-turn budget that prices a guarded object, and the same one
 * `planPrompt` now answers `FIGHT_OR_FLEE` with. One question, three askers,
 * and it is their disagreement that froze the hero.
 *
 * The first step only, and the narrowness is measured rather than tidy. Cutting
 * at *every* guard along a route reads better and plays worse: on a map where
 * every road has something standing on it, it took the 36x36 two-player
 * headline from ten decided of ten to seven, because two computers that both
 * stop at the first monster never meet. Cutting only the first step ends the
 * freeze exactly - a walk that cannot take one step is dropped, and the hero
 * spends his morning on something he can do - while a guard further along is
 * still walked up to and still answered on its merits, which is how a monster
 * worth clearing gets cleared.
 */
type Guard = readonly (ArmySlot | null)[];
const FIGHT_CELLS = new WeakMap<Shadow, ReadonlyMap<number, Guard>>();

/** Cell -> the army standing on it, from the object's public definition. */
export function fightCells(sh: Shadow): ReadonlyMap<number, Guard> {
  const hit = FIGHT_CELLS.get(sh);
  if (hit !== undefined) return hit;
  const m = sh.obs.map;
  const out = new Map<number, Guard>();
  const mark = (x: number, y: number, z: number, guard: Guard): void => {
    if (x < 0 || y < 0 || z < 0 || x >= m.width || y >= m.height || z >= m.levels) return;
    out.set(z * m.width * m.height + y * m.width + x, guard);
  };
  for (const o of sh.objects) {
    const def = sh.content.mapObjects.get(o.def);
    const guard = guardOf(sh, def);
    if (guard === null) continue;
    mark(o.x, o.y, o.z, guard);
    for (const [dx, dy] of def?.footprint ?? []) mark(o.x + dx, o.y + dy, o.z, guard);
  }
  FIGHT_CELLS.set(sh, out);
  return out;
}

/**
 * The guard standing on one cell, as value and as hit points.
 *
 * Read off the object's public definition through `guardOf`, exactly as every
 * other guard assessment here is: the shadow may not look at the live army, and
 * a spell that priced itself from state the player cannot see would be the AI
 * cheating (I9).
 */
function guardAt(
  sh: Shadow, x: number, y: number, z: number,
): { readonly value: number; readonly hp: number } | null {
  const m = sh.obs.map;
  const guard = fightCells(sh).get(z * m.width * m.height + y * m.width + x);
  if (guard === undefined) return null;
  let value = 0, hp = 0;
  for (const slot of guard) {
    if (slot === null) continue;
    const def = sh.content.creatures.get(slot.def);
    if (def === undefined) continue;
    value += def.aiValue * slot.count;
    hp += def.hp * slot.count;
  }
  return hp <= 0 ? null : { value, hp };
}

/** Enemy heroes this Scry would find, by the spell's own reach. */
function enemyHeroesInReach(sh: Shadow, hero: HeroState, def: SpellDef): number {
  const range = SCRY_RANGE_BY_MASTERY[masteryFor(sh.content, hero, def)] ?? 0;
  let n = 0;
  for (const e of sh.enemies) {
    if (e.owner === sh.player) continue;
    const d = sh.state.heroes[e.id as HeroId];
    if (d === undefined) { n++; continue; }
    if (range < 0) { n++; continue; }
    if (d.z === hero.z && tileDistance(hero.x, hero.y, d.x, d.y) <= range) n++;
  }
  return n;
}

/** Cells a lit enemy hero or a town not ours stands on. */
function hostileCells(sh: Shadow): ReadonlySet<number> {
  const out = new Set<number>();
  for (const e of sh.enemies) {
    if (!e.fresh || e.owner === sh.player) continue;
    out.add(e.cell);
  }
  return out;
}

function stopCells(sh: Shadow): ReadonlySet<number> {
  const hit = STOPS_MOVEMENT.get(sh);
  if (hit !== undefined) return hit;
  const m = sh.obs.map;
  const out = new Set<number>();
  const mark = (x: number, y: number, z: number): void => {
    if (x < 0 || y < 0 || z < 0 || x >= m.width || y >= m.height || z >= m.levels) return;
    out.add(z * m.width * m.height + y * m.width + x);
  };
  for (const o of sh.objects) {
    if (o.kind !== "portal") continue;
    mark(o.x, o.y, o.z);
    const def = sh.content.mapObjects.get(o.def);
    for (const [dx, dy] of def?.footprint ?? []) mark(o.x + dx, o.y + dy, o.z);
    for (const [dx, dy] of def?.entrances ?? []) mark(o.x + dx, o.y + dy, o.z);
  }
  STOPS_MOVEMENT.set(sh, out);
  return out;
}

/**
 * The part of a multi-day route that is actually this morning's walk.
 *
 * A route is planned days ahead - that is what `multiDay` buys - but the command
 * handed to the kernel is cut to what the hero can pay for today, for a reason
 * that is not tidiness. A prompt raised part-way along a walk (a level-up, a
 * portal's exit, a guard's parley) carries the *unwalked remainder* in its
 * resume token, and answering it resumes the walk; if the budget ran out at the
 * same step, the resumed step is refused for `INSUFFICIENT_MOVEMENT` and the
 * refusal takes the whole `RESPOND_TO_INTERACTION` with it - so both enumerated
 * answers bounce, the prompt cannot be closed, and the turn deadlocks. Measured
 * on seed 24757: `stalled` on day one, one rejected command, from a level-up
 * offered on the last step the hero could afford.
 *
 * The kernel should end a resumed walk rather than fail it, and this does not
 * make that stop being true. It does mean the AI never asks for the step it
 * cannot pay for, which is the correct plan anyway: tomorrow it re-plans from
 * where it stands, with a day of movement to spend.
 */
function truncateAtBlocked(
  sh: Shadow, p: DifficultyProfile, budget: FightBudget, hero: HeroState,
  set: ReachableSet, path: readonly number[], goal: number,
  jump: PortalLink | null,
): number[] {
  const stops = stopCells(sh);
  const fights = fightCells(sh);
  const hostile = hostileCells(sh);
  const out: number[] = [];
  for (let i = 0; i < path.length; i++) {
    const c = path[i]!;
    if (sh.blockedCells.has(c)) break;
    // A visible enemy hero or town on the way is a fight this route never
    // priced: heroes are not in `map.occupant` and towns are not guards, so
    // the walk used to go straight into a siege on the way to a mine. Eight of
    // thirty-seven battles across three seeds started that way, six of them
    // lost. The goal itself is excepted - `assessConquest` priced that one.
    if (c !== goal && hostile.has(c)) break;
    // A portal mouth is never relaxed *into* - stepping onto it teleports the
    // hero - so its own `cost` stays at infinity and `isReachable` says no
    // however close it is. The cell that carries the answer is the far mouth,
    // which is reachable today exactly when the hero can afford the step onto
    // the near one. Asking the wrong one leaves the AI walking to within a tile
    // of every gate and never going in.
    const stand = jump !== null && i === path.length - 1 ? jump.to : c;
    if (!isReachable(set, stand)) break;
    if (out.length === 0 && c !== goal) {
      const guard = fights.get(c);
      if (guard !== undefined
        && !willFight(assessGuard(sh, p, budget, hero, guard, terrainOf(sh, c)))) break;
    }
    out.push(c);
    if (stops.has(c)) break;
  }
  return out;
}

/**
 * The targets that are errands rather than war, and are damped while the enemy
 * is still missing. `town.defend` is deliberately not one of them: a town about
 * to be taken is worth saving whether or not we know where the thief lives.
 */
const HOME_ERRANDS: ReadonlySet<string> = new Set<string>(["town.collect", "town.restock"]);

/** A verdict the AI acts on. "costly" is still a win, and "unknown" is not. */
export const willFight = (v: FightVerdict): boolean => v === "win" || v === "costly";

/** Terrain under a cell, for the battlefield a probe should be fought on. */
function terrainOf(sh: Shadow, cell: number): TerrainKind {
  return TERRAINS[sh.obs.map.terrain[cell] ?? 0] ?? "grass";
}

/**
 * Score one hero's whole reachable region against the shared target list.
 *
 * `limit` caps the work: targets arrive sorted by raw value, so cutting the tail
 * drops the least interesting ones first and an easy AI simply looks at fewer.
 */
function scoreHero(
  sh: Shadow, p: DifficultyProfile, budget: FightBudget,
  heroId: HeroId, set: ReachableSet, targets: readonly Target[],
  front: readonly { cell: number; unseen: number }[],
  hunting: boolean,
): Scored[] {
  const hero = sh.state.heroes[heroId];
  if (hero === undefined) return [];
  const visited = new Set<string>(hero.visited as readonly string[]);
  const out: Scored[] = [];
  const seen = new Set<number>();
  // What this hero is carrying, for the one question a pickup asks about him.
  const ours = Math.max(1, armyStrength(sh.content, sh.state.armies[hero.army] ?? null));

  /** Cheapest cell in `cells` the hero can actually stand on, or -1. */
  const nearest = (cells: readonly number[]): number => {
    let best = -1, bestCost = UNREACHABLE_COST;
    for (const c of cells) {
      if (sh.blockedCells.has(c)) continue;
      const cost = pathCost(set, c);
      if (cost === UNREACHABLE_COST) continue;
      if (!p.multiDay && !isReachable(set, c)) continue;
      if (cost < bestCost || (cost === bestCost && c < best)) { bestCost = cost; best = c; }
    }
    return best;
  };

  // Scoring reads costs out of the Dijkstra table; it does not build the route.
  // A route is O(its length) to walk back and only one of a hundred candidates
  // ever becomes a command, so the path is built after the assignment instead.
  const push = (
    cells: readonly number[], value: number, why: string, patience = 1,
  ): void => {
    if (value <= 0) return;
    const cell = nearest(cells);
    if (cell < 0) return;
    const walk = patience > 1
      ? Math.floor(pathCost(set, cell) / patience)
      : pathCost(set, cell);
    const score = Math.floor((value * 1000) / (walk + DISTANCE_BASE));
    if (score <= 0) return;
    out.push({ hero: heroId, target: cell, score, why, set });
  };

  let looked = 0;
  for (const t of targets) {
    if (looked >= p.candidateLimit) break;
    if (t.onlyHero !== null && t.onlyHero !== heroId) continue;
    if (t.object !== null && t.oneVisitPerHero && visited.has(t.object as string)) continue;
    if (seen.has(t.cell)) continue;
    seen.add(t.cell);
    looked++;

    let value = t.value;
    if (hunting && HOME_ERRANDS.has(t.why) && t.waiting < MASSING_MULTIPLE * ours) {
      value = Math.floor((value * HUNT_ERRAND_BP) / 10000);
    }
    if (t.guard !== null) {
      const v = assessGuard(sh, p, budget, hero, t.guard, terrainOf(sh, t.cell));
      if (v === "lose" || v === "unknown") continue;
      if (v === "costly") value = Math.floor((value * COSTLY_BP) / 10000);
    } else if (t.unknownFight) {
      const v = assessConquest(sh, p, budget, hero, t, terrainOf(sh, t.cell));
      if (v === "lose" || v === "unknown") continue;
      if (v === "costly") value = Math.floor((value * COSTLY_BP) / 10000);
      if (t.sighting !== null
        && looksFirst(
          armyStrength(sh.content, sh.state.armies[hero.army] ?? null),
          presumedWorth(sh, t))) {
        push(t.sighting, value, `${t.why}.sight`, t.patience);
        continue;
      }
    }
    push(t.approach, value, t.why, t.patience);
  }

  const unit = hunting ? Math.floor((FRONTIER_UNIT * SCOUT_WAR_BP) / 10000) : FRONTIER_UNIT;
  const patience = hunting ? p.conquestPatience : 1;
  for (const f of front) {
    if (seen.has(f.cell)) continue;
    // Cheap search only explores what it can reach today; everything else may
    // walk for days toward the dark, which is how a computer finds a capital it
    // has never seen.
    if (!p.multiDay && !isReachable(set, f.cell)) continue;
    seen.add(f.cell);
    push([f.cell], f.unseen * unit, hunting ? "scout" : "explore", patience);
  }

  return out;
}

/**
 * The strategic plan for the whole turn: one destination per hero, no two heroes
 * sent to the same tile. Greedy over the sorted score list, which is stable and
 * good enough - a Hungarian assignment over eight heroes and two hundred targets
 * would cost more than it wins.
 */
export function planAdventure(
  sh: Shadow, p: DifficultyProfile, legal: readonly LegalCommand[],
  budget: FightBudget, short: Shortage, signal?: AbortSignal,
): Candidate[] {
  const targets = collectTargets(sh, short, p);
  const front = frontier(sh);
  // Nobody has found a rival capital yet, so finding one is the campaign.
  //
  // A hall that belongs to nobody is not a rival capital, and until this asked
  // `rivalHeld` it counted as one. Measured on 72x72 seed 32676: `p1` saw a
  // neutral hall on day ten, called the war found, and spent the remaining 290
  // days with **84% of the map still black**, having never once seen a rival
  // town - while `p2`, which had captured that hall and so had no unowned town
  // in sight, went on hunting and got the map down to 68%. Neither side ever
  // found the other and the game ran to the ceiling. Seeing an empty fort is
  // not finding the enemy.
  let hunting = !warFound(sh);
  // And a capital nobody can walk to is a capital nobody can take. `observe`
  // publishes a town on any explored cell because a town is a landmark, but the
  // shadow's movement plane is zero everywhere the fog still sits - so a town
  // seen across unexplored ground is *known* and *unreachable* at the same
  // time, and the frontier that would open the road to it is priced at 350 a
  // cell because the search was called off the moment the town was named.
  // Measured on seed 4242 at day 250: `p3` held 1.3M of army, the last town of
  // a rival sixteen tiles away, its own probe calling the siege a **win** - and
  // `walk=UNREACH`, so it scored zero and the game ran to the ceiling. Finding
  // the road is the same campaign as finding the town.
  if (!hunting) hunting = !anyConquestReachable(sh, legal);

  const all: Scored[] = [];
  for (const c of legal) {
    if (!isMoveFamily(c)) continue;
    if (signal?.aborted === true) break;
    all.push(...scoreHero(
      sh, p, budget, c.heroId, routeSet(sh, c.heroId, c.reachable), targets, front, hunting));
  }

  all.sort((a, b) =>
    (b.score - a.score)
    || (a.hero < b.hero ? -1 : a.hero > b.hero ? 1 : 0)
    || (a.target - b.target));

  const usedHero = new Set<string>();
  const usedTarget = new Set<number>();
  const out: Candidate[] = [];
  for (const s of all) {
    if (usedHero.has(s.hero as string) || usedTarget.has(s.target)) continue;
    const marching = sh.state.heroes[s.hero];
    if (marching === undefined) continue;
    // The first leg, not the whole route: `pathLegs` cuts at every portal, and
    // a leg is 8-adjacent on one plane, which is exactly what `MOVE_HERO`
    // accepts. Stepping onto the mouth spends the rest of the day, so the days
    // after this one are re-planned tomorrow from wherever the jump landed.
    const legs = pathLegs(s.set, s.target);
    const path = truncateAtBlocked(
      sh, p, budget, marching, s.set, legs[0]?.cells ?? [], s.target,
      legs[0]?.link ?? null,
    );
    if (path.length === 0) continue;
    usedHero.add(s.hero as string);
    usedTarget.add(s.target);
    out.push({
      command: { type: "MOVE_HERO", hero: s.hero, path } satisfies GameCommand,
      score: s.score,
      why: s.why,
    });
  }
  return out;
}

/**
 * Can any hero of ours cost a route to a town a live rival holds?
 *
 * The mitigation for "the capital is known and unreachable", and it was doing
 * harm as well as good: it asked about every target called `enemy.town`, which
 * includes every ownerless hall on the board, so a neutral fort on explored
 * ground certified that the war had been found *and* was walkable. Restricted
 * to towns a rival actually holds it says what it was always meant to say.
 *
 * It also fires less often than it used to, because the route it asks about is
 * now the whole-map route: a capital on the far side of a subterranean gate the
 * kingdom has been through is reachable, and used to be neither.
 */
function anyConquestReachable(sh: Shadow, legal: readonly LegalCommand[]): boolean {
  const cells: number[] = [];
  for (const e of sh.enemies) if (rivalHeld(sh, e) && !sh.blockedCells.has(e.cell)) cells.push(e.cell);
  if (cells.length === 0) return false;
  for (const c of legal) {
    if (!isMoveFamily(c)) continue;
    const set = routeSet(sh, c.heroId, c.reachable);
    for (const cell of cells) if (pathCost(set, cell) !== UNREACHABLE_COST) return true;
  }
  return false;
}

/**
 * The shovel.
 *
 * `DIG` is legal for any hero standing on open ground at full movement, which
 * is most heroes on most mornings, so this scores exactly one of them and gives
 * every other one nothing. Digging anywhere but the buried cell costs a whole
 * day of movement and leaves a hole in the ground.
 *
 * The kernel's own gate is `digIllegality`, and the command is only in `legal`
 * when that gate passed - so what is decided here is only whether the hole is
 * worth digging, never whether it may be.
 *
 * Priced at `GRAIL_WORTH` undiscounted, unlike the obelisks: the search is over,
 * the hero is standing on it, and the only thing between the kingdom and the
 * prize is one command.
 */
export function planDig(sh: Shadow, legal: readonly LegalCommand[]): Candidate[] {
  const pz = sh.obs.puzzle;
  if (pz === null || pz.cell < 0) return [];
  const out: Candidate[] = [];
  for (const c of legal) {
    if (c.type !== "DIG") continue;
    const h = sh.state.heroes[c.hero];
    if (h === undefined || h.z !== 0) continue;
    const here = h.z * sh.obs.map.width * sh.obs.map.height + h.y * sh.obs.map.width + h.x;
    if (here !== pz.cell) continue;
    out.push({ command: c, score: GRAIL_WORTH, why: "grail.dig" });
  }
  return out;
}

/**
 * Objects the hero is already standing next to. Free value: no movement is
 * spent, so these are scored as if the distance were zero and always beat a
 * walk of equal worth.
 */
export function planVisits(
  sh: Shadow, legal: readonly LegalCommand[], short: Shortage,
): Candidate[] {
  const byId = new Map<string, ObservedObject>();
  for (const o of sh.objects) byId.set(o.id as string, o);

  const out: Candidate[] = [];
  for (const c of legal) {
    if (c.type !== "VISIT_OBJECT") continue;
    const o = byId.get(c.object as string);
    if (o === undefined) continue;
    // Never walk into a guard by clicking it: that decision belongs to the
    // movement scorer, which has a probe budget and knows the hero's army.
    const def = sh.content.mapObjects.get(o.def);
    if (def !== undefined && guardOf(sh, def) !== null) continue;
    // A portal is not loot, it is a road. `VISIT_OBJECT` on one teleports the
    // hero, and a road scored as if standing next to it were the reward is a
    // road the AI walks up and down for the rest of the game. Stepping onto it
    // triggers it anyway, so the movement scorer owns this entirely.
    if (o.kind === "portal") continue;
    const value = objectValue(sh, o, short);
    if (value <= 0) continue;
    out.push({
      command: c, score: Math.floor((value * 1000) / DISTANCE_BASE), why: `visit.${o.kind}`,
    });
  }
  out.sort((a, b) => b.score - a.score);
  return out;
}

// ------------------------------------------------------------ adventure magic
//
// Adventure spells are not a fourth kind of action, they are movement bought
// with mana, so they are scored in the currency movement already has: the
// gold-equivalent of a movement point. Homeward Gate and Blink Step are worth
// what walking the same distance would have cost, less what they cost - the day
// the portal forfeits, the three tiles the door charges - and if that difference
// is small the AI walks, which is the correct answer.
//
// I9 holds by the same route it holds everywhere else here: the hero, its
// spellbook, its Wisdom and the fog all come out of the shadow, and the only
// cells a door may open onto are cells the observation says are lit *now*.

/** One plain tile of walking is 100 movement points; call it 200 gold. */
export const GOLD_PER_MOVEMENT = 2;
/** A portal that saves less than this many tiles is not worth a day and 25 mana. */
export const PORTAL_MIN_TILES = 8;
/** A door has to save more than four tiles of walking to be worth the mana. */
export const DOOR_MIN_GAIN = 400;
/** What walking *around* something costs, against the straight-line distance. */
export const UNREACHABLE_DETOUR = 3;
/** Ground a scrying spell is worth uncovering, at a wholly dark map. */
export const VIEW_VALUE = 6000;
/** What one enemy hero's position is worth finding, for Scry. */
export const SCRY_VALUE_PER_HERO = 900;
/** What one spell point is worth, for a ritual that trades creatures for them. */
export const MANA_POINT_VALUE = 120;
/** Below this much room in the pool, a ritual is pouring mana on the ground. */
export const BLOOD_RITUAL_MIN_ROOM = 10;
/** Mana a hero keeps back for a battle, whatever the map still hides. */
export const SCRY_MANA_FLOOR = 12;
/** Targets a door looks at before it settles for the best it has found. */
export const DOOR_TARGET_LIMIT = 8;

/**
 * Cells a Blink Step may not land on, over and above the ones the kernel
 * can see for itself.
 *
 * The shadow's occupancy plane is empty by construction - `shadow.ts` does not
 * rebuild it, because the pathfinder reads `moveCost` instead - so a dry run
 * against the shadow would happily land a hero on a resource pile that the real
 * kernel refuses. The observation does carry every object and every hero on
 * explored ground, so the footprints are rebuilt here rather than trusted.
 */
function landingRefusals(sh: Shadow): ReadonlySet<number> {
  const m = sh.obs.map;
  const out = new Set<number>(sh.blockedCells);
  const mark = (x: number, y: number, z: number): void => {
    if (x < 0 || y < 0 || z < 0 || x >= m.width || y >= m.height || z >= m.levels) return;
    out.add(z * m.width * m.height + y * m.width + x);
  };
  for (const o of sh.objects) {
    mark(o.x, o.y, o.z);
    for (const [dx, dy] of sh.content.mapObjects.get(o.def)?.footprint ?? []) {
      mark(o.x + dx, o.y + dy, o.z);
    }
  }
  for (const h of sh.obs.heroes) mark(h.x, h.y, h.z);
  for (const t of sh.obs.towns) mark(t.x, t.y, t.z);
  for (const e of sh.enemies) mark(e.x, e.y, e.z);
  return out;
}

/** Basis points of the map that is still dark. A scry is worth nothing at zero. */
function darkFractionBp(sh: Shadow): number {
  const vis = sh.obs.map.visibility;
  let dark = 0;
  for (let i = 0; i < vis.length; i++) if (vis[i] === 0) dark++;
  return vis.length === 0 ? 0 : Math.floor((dark * 10000) / vis.length);
}

/** The town an enumerated Homeward Gate command is aimed at, or null. */
function townAt(sh: Shadow, x: number, y: number, z: number): TownState | null {
  for (const t of sh.obs.towns) {
    if (t.detail === null) continue;
    if (t.x === x && t.y === y && t.z === z) return t.detail;
  }
  return null;
}

/**
 * The route a hero would have to walk to stand where a spell would put him.
 *
 * In movement points, the currency `pathCost` and `hero.movementLeft` are both
 * already in, and taken off the *route* planner rather than off a straight
 * line: the whole point of a portal is the march it replaces, and the march is
 * the one this kingdom would actually have to make - round the lake, through
 * the gate it has learned, on the road where there is one.
 *
 * A route this kingdom cannot cost - the ground between here and home is still
 * dark, or the only road round the bay has not been walked - is not priced as
 * a detour the way `scoreDoorCasts` prices one. It is priced at the straight
 * line, which is the *floor* on what the walk could possibly cost, because
 * overstating it is how a spell that forfeits a day gets cast to save half a
 * morning: every one of these is a town we own, and the fog over the ground in
 * front of it is not evidence that the way round is long.
 */
function walkCostTo(
  hero: HeroState, set: ReachableSet | null, cell: number, x: number, y: number,
): number {
  const straight = tileDistance(hero.x, hero.y, x, y) * DISTANCE_BASE;
  const cost = set === null ? UNREACHABLE_COST : pathCost(set, cell);
  return cost === UNREACHABLE_COST ? straight : cost;
}

/**
 * What a Homeward Gate is worth.
 *
 * It used to be worth the walk it replaces and nothing else, which prices it as
 * a cheaper pair of boots. That is not what the spell is for. Its value is that
 * it **collapses the return leg**: a hero takes a town at the far end of the
 * map, portals home in an afternoon, picks up the week's recruits and the
 * garrison, and marches back - and the campaign that was one journey each way
 * becomes one journey. Scored as a walk, that whole manoeuvre is worth twenty
 * tiles of road; scored properly it is worth the army waiting at the other end,
 * which is what makes the hero willing to be at the far end in the first place.
 *
 * So the price is the march it saves *plus* what is waiting where it lands: the
 * garrison this kingdom owns there, the recruits standing in the hall, and - the
 * case a walk can never answer in time - a town of ours with an enemy hero
 * bearing down on it and nobody inside.
 *
 * The subtraction stays. The hero arrives with no movement left, so a portal to
 * a town he could have walked to today is a day thrown away, and
 * `PORTAL_MIN_TILES` is where that stops being true.
 *
 * The march it replaces is measured by the route planner and not by a straight
 * line (`walkCostTo`). A straight line is a second notion of distance sitting
 * next to the Dijkstra the rest of this file scores against, and it is wrong in
 * both directions at once: it undercharges the walk round a bay, which is where
 * a portal is worth most, and overcharges the walk down a road, which is where
 * it is worth least. On the road that matters by a factor of two - a cobblestone
 * step is 50 movement against a plain tile's 100 - so the same twenty tiles are
 * ten tiles of portal value or twenty, depending on whether the hero would have
 * had a road to walk them on.
 */
function portalValue(
  sh: Shadow, p: DifficultyProfile, hero: HeroState, town: TownState,
  set: ReachableSet | null,
): number {
  const m = sh.obs.map;
  const cell = town.z * m.width * m.height + town.y * m.width + town.x;
  const walk = Math.floor(walkCostTo(hero, set, cell, town.x, town.y) / DISTANCE_BASE);
  const couldWalk = Math.floor(hero.movementLeft / DISTANCE_BASE);
  const net = walk - couldWalk;
  if (net < PORTAL_MIN_TILES) return 0;
  let value = net * DISTANCE_BASE * GOLD_PER_MOVEMENT;

  const garrison = sh.state.armies[town.garrison] ?? null;
  if ((garrison?.owner ?? null) === sh.player) {
    const held = armyStrength(sh.content, garrison);
    if (held >= COLLECT_MIN) {
      value += Math.min(COLLECT_CAP, Math.floor((held * COLLECT_BP) / 10000));
    }
  }

  let waiting = 0;
  for (const k of Object.keys(town.available).sort()) {
    const n = town.available[k] ?? 0;
    const c = sh.content.creatures.get(k as CreatureDefId);
    if (c !== undefined && n > 0) waiting += c.aiValue * n;
  }
  value += Math.floor((waiting * OWN_TOWN_RESTOCK_BP) / 10000);

  for (const t of threatenedTowns(sh, p)) {
    if (t.town !== town.id || !t.undefended) continue;
    value += reliefAdjusted(t.stake, t.relief);
  }
  return value;
}

/**
 * Score the adventure casts the kernel already enumerated.
 *
 * Everything here came out of `enumerateLegalCommands`, so an unaffordable cast,
 * a Fly that is already up or a far town below expert mastery was never offered
 * (3.3). What is decided here is only whether the cast is worth making.
 */
function scoreEnumeratedCasts(
  sh: Shadow, p: DifficultyProfile, legal: readonly LegalCommand[],
): Candidate[] {
  const out: Candidate[] = [];
  let dark = -1;
  // Built only if a Homeward Gate is actually on offer, which is most of no
  // decisions: `routeSet` is a Dijkstra pass, and the enumerator has already
  // paid for one per hero that this can borrow when there are no portal links.
  const enumerated = new Map<string, ReachableSet>();
  const routeFor = (h: HeroId): ReachableSet | null => {
    if (enumerated.size === 0) {
      for (const c of legal) if (isMoveFamily(c)) enumerated.set(c.heroId as string, c.reachable);
    }
    const hit = enumerated.get(h as string);
    return hit === undefined ? null : routeSet(sh, h, hit);
  };
  for (const c of legal) {
    if (c.type !== "CAST_ADVENTURE_SPELL") continue;
    const hero = sh.state.heroes[c.hero];
    const def = sh.content.spells.get(c.spell);
    if (hero === undefined || def === undefined) continue;
    const rule = adventureRuleOf(def);
    let value = 0;
    let why = "";
    switch (rule) {
      case "adventure.homeward_gate": {
        const town = townAt(sh, c.x, c.y, c.z);
        if (town === null) break;
        value = portalValue(sh, p, hero, town, routeFor(c.hero));
        why = "spell.homeward_gate";
        break;
      }
      case "adventure.visions":
        // Visions lights the ground under the guarded objects in a short radius
        // and nothing else. It is not a scry of the map and it is not scored as
        // one; what a guard is made of comes from the object's public
        // definition (`guardOf`), which the planner can already read.
        break;
      case "adventure.wind_sight":
      case "adventure.earth_sight": {
        // A scry is only ever worth the ground it uncovers, and it must not eat
        // the mana a portal is being saved for.
        //
        // And it is only worth the ground it can *reach*. Below expert this
        // spell does not scry the map at all - `view` in `spells.ts` lights one
        // cell per matching object already inside `VIEW_RANGE_BY_MASTERY`, and
        // only the expert arm, whose range is -1 for "across the whole map",
        // calls `revealAll`. Priced at the whole dark fraction whatever the
        // mastery, a basic scry was the best thing on the board every single
        // morning: measured on 72x72 seed 32676, **571 casts by one kingdom in
        // one game**, the map still 68% black at the end of it, and the mana
        // gone that Homeward Gate needed.
        //
        // So the dark fraction is scaled by the share of the map the range can
        // touch. An expert cast keeps its full price and correctly prices
        // itself out afterwards - `dark` is near zero the second time it is
        // asked - and a basic one is worth the postage stamp it is.
        if (dark < 0) dark = darkFractionBp(sh);
        const cost = spellCostFor(makeCtx(sh.state, sh.content), hero, c.spell);
        if (hero.mana - cost < SCRY_MANA_FLOOR) break;
        const range = VIEW_RANGE_BY_MASTERY[masteryFor(sh.content, hero, def)] ?? 0;
        const plane = sh.obs.map.width * sh.obs.map.height;
        const reach = range < 0 || plane <= 0
          ? 10000
          : Math.min(10000, Math.floor(((2 * range + 1) * (2 * range + 1) * 10000) / plane));
        value = Math.floor((VIEW_VALUE * dark * reach) / 100000000);
        why = `spell.${rule.slice("adventure.".length)}`;
        break;
      }
      /**
       * Harvest and Transmute are paid in resources, so they are priced the way
       * every other resource on the board is - through `resourceValue`, the one
       * table that says what a unit of ore is worth against a unit of gold.
       * Both are once a day and the enumerator stops offering them once spent,
       * so there is no need to remember here that they were cast.
       */
      case "adventure.harvest": {
        const bulk = HARVEST_BY_MASTERY[masteryFor(sh.content, hero, def)] ?? 1;
        value = bulk * ((sh.content.resourceValue.wood ?? 1) + (sh.content.resourceValue.ore ?? 1));
        why = "spell.reaping";
        break;
      }
      case "adventure.transmute": {
        const rate = TRANSMUTE_GOLD_PER_UNIT[masteryFor(sh.content, hero, def)] ?? 0;
        const from = transmuteSource(makeCtx(sh.state, sh.content), hero);
        if (from === null) break;
        const given = TRANSMUTE_UNITS * (sh.content.resourceValue[from as ResourceKind] ?? 1);
        value = TRANSMUTE_UNITS * rate - given;
        why = "spell.transmute";
        break;
      }
      /**
       * Blood Rite is only worth casting when the pool it fills is the thing
       * standing in the way. A hero near full mana would be trading creatures
       * for nothing - the ritual caps at the maximum - so the gain is measured
       * against the room actually left.
       */
      case "adventure.blood_rite": {
        const ctx = makeCtx(sh.state, sh.content);
        const room = maxMana(ctx, hero) - hero.mana;
        if (room < BLOOD_RITUAL_MIN_ROOM) break;
        const slot = bloodRitualSlot(ctx, hero, masteryFor(sh.content, hero, def));
        if (slot === null) break;
        const army = sh.state.armies[hero.army];
        const stack = army?.slots[slot] ?? null;
        if (stack === null) break;
        const creature = sh.content.creatures.get(stack.def);
        const offered = BLOOD_RITUAL_CREATURES[masteryFor(sh.content, hero, def)] ?? 1;
        const gained = Math.min(room, offered * (creature?.tier ?? 1) * BLOOD_RITUAL_MANA_PER_TIER);
        value = gained * MANA_POINT_VALUE - offered * (creature?.aiValue ?? 0);
        why = "spell.blood_rite";
        break;
      }
      /**
       * Scry is priced like the view spells and for the same reason: what it is
       * worth is the fog it lifts. It lifts a ring around each enemy hero
       * rather than a disc around the caster, so the price is per hero found
       * and not a fraction of the map.
       */
      case "adventure.scry": {
        const cost = spellCostFor(makeCtx(sh.state, sh.content), hero, c.spell);
        if (hero.mana - cost < SCRY_MANA_FLOOR) break;
        value = SCRY_VALUE_PER_HERO * enemyHeroesInReach(sh, hero, def);
        why = "spell.scry";
        break;
      }
      /**
       * Meteor Strike is worth what it destroys, less nothing: the guard does
       * not strike back and the hero does not move. The only reason not to cast
       * it is the mana, which the enumerator has already checked.
       */
      case "adventure.meteor_strike": {
        const guard = guardAt(sh, c.x, c.y, c.z);
        if (guard === null) break;
        const power = hero.primary.spellPower;
        const hp = (METEOR_HP_PER_POWER[masteryFor(sh.content, hero, def)] ?? 0) * power;
        value = Math.min(guard.value, Math.floor((guard.value * hp) / Math.max(1, guard.hp)));
        why = "spell.meteor_strike";
        break;
      }
      default:
        // Fly, Wave Stride, Call Vessel, Sink Vessel and Disguise are legal and
        // enumerated, and are left unscored: what they are worth is what they
        // unlock, and that is a second Dijkstra under a different `MoveMode`
        // which this planner does not run. Scoring them at a guess would have
        // the AI casting Fly every morning for nothing.
        //
        // Recall and Tunnel join them, for the same reason and one more: both
        // move a hero, and what a move is worth is the route it opens. Recall
        // also spends a SECOND hero's whole day, which no scorer here can see
        // the cost of. They are the player's spells until the planner can run
        // that second search.
        break;
    }
    if (value <= 0) continue;
    out.push({
      command: c, score: Math.floor((value * 1000) / DISTANCE_BASE), why,
    });
  }
  return out;
}

/**
 * Blink Step, which the kernel deliberately does not enumerate: its target
 * is one cell out of tens of thousands and picking it is the caller's job, the
 * same split `MOVE_HERO` uses.
 *
 * The door is aimed at whatever the hero most wants to reach and cannot cheaply
 * walk to, and it is worth the movement it saves. Every candidate is put back
 * through `executeCommand` on the shadow before it is offered, so the contract
 * the enumerator gives the rest of the planner - what you are handed executes -
 * holds for the one command the planner invented itself.
 */
function scoreDoorCasts(
  sh: Shadow, p: DifficultyProfile, budget: FightBudget,
  legal: readonly LegalCommand[], short: Shortage,
): Candidate[] {
  const c = makeCtx(sh.state, sh.content);
  const doors: { hero: HeroId; spell: SpellDefId; range: number; set: ReachableSet }[] = [];
  for (const cmd of legal) {
    if (!isMoveFamily(cmd)) continue;
    const hero = sh.state.heroes[cmd.heroId];
    if (hero === undefined) continue;
    for (const spell of hero.spellbook) {
      const def = sh.content.spells.get(spell);
      if (def === undefined || adventureRuleOf(def) !== "adventure.blink_step") continue;
      if (castGate(c, hero, spell) !== null) continue;
      doors.push({
        hero: cmd.heroId, spell,
        range: dimensionDoorRange(c, masteryFor(sh.content, hero, def)),
        set: routeSet(sh, cmd.heroId, cmd.reachable),
      });
    }
  }
  if (doors.length === 0) return [];

  const m = sh.obs.map;
  const refused = landingRefusals(sh);
  const targets = collectTargets(sh, short, p);
  const out: Candidate[] = [];

  for (const door of doors) {
    const hero = sh.state.heroes[door.hero]!;
    let bestGain = 0, bestX = -1, bestY = -1;

    let looked = 0;
    for (const t of targets) {
      if (looked >= DOOR_TARGET_LIMIT) break;
      if (t.z !== hero.z) continue;
      if (t.onlyHero !== null && t.onlyHero !== door.hero) continue;
      looked++;
      if (t.guard !== null) {
        const v = assessGuard(sh, p, budget, hero, t.guard, terrainOf(sh, t.cell));
        if (v === "lose" || v === "unknown") continue;
      }
      // What the hero would spend walking. An unreachable target is not
      // infinitely far, it is round a corner: price it as a detour.
      let walk = UNREACHABLE_COST;
      for (const cell of t.approach) {
        const cost = pathCost(door.set, cell);
        if (cost < walk) walk = cost;
      }
      if (walk === UNREACHABLE_COST) {
        walk = tileDistance(hero.x, hero.y, t.x, t.y) * DISTANCE_BASE * UNREACHABLE_DETOUR;
      }
      if (walk <= DIMENSION_DOOR_MOVEMENT_COST + DOOR_MIN_GAIN) continue;

      for (let y = hero.y - door.range; y <= hero.y + door.range; y++) {
        for (let x = hero.x - door.range; x <= hero.x + door.range; x++) {
          if (x < 0 || y < 0 || x >= m.width || y >= m.height) continue;
          const d = tileDistance(hero.x, hero.y, x, y);
          if (d === 0 || d > door.range) continue;
          const cell = hero.z * m.width * m.height + y * m.width + x;
          // Lit *now*, not merely explored: an enemy hero standing on ground the
          // AI only remembers is invisible to it and the kernel would refuse the
          // landing. The fog is the reason the door is short-sighted, and that
          // is the correct trade for never issuing a command that bounces.
          if (m.visibility[cell] !== 2 || (m.moveCost[cell] ?? 0) <= 0) continue;
          if (refused.has(cell)) continue;
          const after = DIMENSION_DOOR_MOVEMENT_COST
            + tileDistance(x, y, t.x, t.y) * DISTANCE_BASE;
          const gain = walk - after;
          if (gain > bestGain) { bestGain = gain; bestX = x; bestY = y; }
        }
      }
    }

    if (bestGain < DOOR_MIN_GAIN || bestX < 0) continue;
    const command: GameCommand = {
      type: "CAST_ADVENTURE_SPELL",
      hero: door.hero, spell: door.spell, x: bestX, y: bestY, z: hero.z,
    };
    if (!executeCommand(sh.state, command, sh.content).ok) continue;
    out.push({
      command,
      score: Math.floor((bestGain * GOLD_PER_MOVEMENT * 1000) / DISTANCE_BASE),
      why: "spell.blink_step",
    });
  }
  return out;
}

/** Every adventure cast worth making this decision, best first. */
export function planSpells(
  sh: Shadow, p: DifficultyProfile, legal: readonly LegalCommand[],
  budget: FightBudget, short: Shortage,
): Candidate[] {
  const out = [
    ...scoreEnumeratedCasts(sh, p, legal),
    ...scoreDoorCasts(sh, p, budget, legal, short),
  ];
  out.sort(compareCandidates);
  return out;
}
